import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import propertyService from '../../services/propertyService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SimplifiedOverview from '../analysis-results/components/SimplifiedOverview';
import EquityChart from '../analysis-results/components/EquityChart';
import ProjectionTable from '../analysis-results/components/ProjectionTable';
import AIAnalysisSummary from '../analysis-results/components/AIAnalysisSummary';
import PropertyAIAnalysis from '../analysis-results/components/PropertyAIAnalysis';
import { calculateOverviewKPIs, generateOverviewProjectionData } from '../../utils/propertyKpis';

/**
 * Page visiteur dédiée - /shared?token=XXX
 * Affiche toutes les données d'analyse (vue d'ensemble + analyse avancée + commentaires IA)
 * à partir de la même ligne propriété (pas de duplication). CTA selon connexion.
 */
const SharedAnalysis = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [aiAnalysisData, setAiAnalysisData] = useState(null); // Contenu analysis_data pour affichage IA
  const [addingToMine, setAddingToMine] = useState(false);
  const [addError, setAddError] = useState('');

  const shareToken = searchParams.get('token') || searchParams.get('id');

  useEffect(() => {
    const load = async () => {
      if (!shareToken) {
        setError('Lien de partage invalide');
        setLoading(false);
        return;
      }

      try {
        const result = await propertyService.getByShareToken(shareToken);
        if (!result?.success || !result?.data) {
          throw new Error(result?.error || 'Analyse non trouvée ou lien expiré');
        }

        const data = result.data;
        setProperty(data);
        // Commentaires d'analyse OpenAI (chargés dans getByShareToken pour respecter RLS partagé)
        setAiAnalysisData(data?.ai_analysis_data ?? null);

        document.title = `Analyse: ${data?.address || 'Propriété'} - ImmoIQ`;
        const ogTitle = document.querySelector('meta[property="og:title"]');
        const ogDesc = document.querySelector('meta[property="og:description"]');
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogTitle) ogTitle.setAttribute('content', document.title);
        if (ogDesc) ogDesc.setAttribute('content', 'Résultats de cette analyse immobilière - ImmoIQ');
        if (ogUrl) ogUrl.setAttribute('content', window.location.href);

        const { data: scenariosRows } = await supabase
          .from('property_scenarios')
          .select('*')
          .eq('property_id', data.id);
        setScenarios(scenariosRows || []);
      } catch (err) {
        console.error('Error loading shared property:', err);
        setError(err?.message || 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [shareToken]);

  const kpis = useMemo(() => calculateOverviewKPIs(property), [property]);
  const projectionData = useMemo(() => {
    if (!property || !kpis) return [];
    return generateOverviewProjectionData(property, kpis, 10);
  }, [property, kpis]);

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'LayoutDashboard' },
    { id: 'projections', label: 'Analyse avancée', icon: 'TrendingUp' }
  ];

  const handleAddToMyProperties = async () => {
    if (!user) {
      sessionStorage.setItem('import_shared_analysis', shareToken);
      navigate('/login');
      return;
    }
    if (!property || addingToMine) return;
    setAddingToMine(true);
    setAddError('');
    try {
      const result = await propertyService.duplicateFromShare(
        { property, scenarios, aiAnalysisData },
        user.id
      );
      if (result?.success && result?.data?.id) {
        navigate(`/analysis-results/${result.data.id}`, { replace: true });
        return;
      }
      setAddError(result?.error || 'Impossible d\'ajouter la propriété');
    } catch (err) {
      console.error('Add to my properties:', err);
      setAddError(err?.message || 'Erreur lors de l\'ajout');
    } finally {
      setAddingToMine(false);
    }
  };

  const handleTryAnalyze = () => {
    navigate('/property-analysis-wizard');
  };

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Chargement de l'analyse...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">Erreur</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
        </div>
      </div>
    );
  }

  const ctaLabel = user ? 'Ajouter à mes propriétés' : 'Essayer d\'analyser une propriété';
  const ctaAction = user ? handleAddToMyProperties : handleTryAnalyze;

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      {/* Bannière visiteur + CTA */}
      <div
        className="sticky top-0 z-50 flex items-center justify-between gap-4 py-3 px-4 flex-wrap"
        style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, #1e40af 100%)', color: 'white' }}
      >
        <div className="flex items-center gap-2">
          <Icon name="Eye" size={18} />
          <span className="text-sm font-medium">Cette analyse a été partagée avec vous</span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          iconName={addingToMine ? 'Loader2' : user ? 'Plus' : 'BarChart3'}
          iconPosition="left"
          onClick={ctaAction}
          disabled={addingToMine}
          style={{ background: 'white', color: 'var(--color-primary)' }}
        >
          {addingToMine ? 'Ajout en cours...' : ctaLabel}
        </Button>
      </div>

      {/* En-tête propriété */}
      <header className="border-b border-border bg-card px-4 py-4 md:px-6">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--color-primary)' }}
          >
            <Icon name="Building2" size={24} color="white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">{property?.address}</h1>
            <p className="text-sm text-muted-foreground">
              {property?.city}
              {property?.province && `, ${property.province}`}
              {property?.property_type && ` • ${property.property_type}`}
            </p>
          </div>
        </div>
      </header>

      {/* Onglets + contenu */}
      <div className="max-w-5xl mx-auto px-4 py-6 md:px-6">
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium border transition-colors ${activeTab === tab.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-muted-foreground border-border hover:bg-muted'
                }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && property && kpis && (
          <div className="space-y-6">
            {aiAnalysisData && (
              <AIAnalysisSummary
                property={property}
                analysis={aiAnalysisData}
                summaryMode={true}
                isReadOnly={true}
              />
            )}
            <SimplifiedOverview
              property={property}
              kpis={kpis}
              projectionData={projectionData}
            />
          </div>
        )}

        {activeTab === 'projections' && (
          <div className="space-y-6">
            {aiAnalysisData && (
              <PropertyAIAnalysis
                property={property}
                analysis={aiAnalysisData}
                isReadOnly={true}
              />
            )}
            {projectionData.length > 0 && (
              <>
                <div className="bg-card border border-border rounded-xl p-4">
                  <h2 className="text-lg font-semibold mb-4">Projection sur 10 ans</h2>
                  <EquityChart data={projectionData} projectionYears={10} />
                </div>
                <ProjectionTable data={projectionData} projectionYears={10} />
              </>
            )}
          </div>
        )}
      </div>

      {/* CTA bas de page */}
      <div className="px-4 mt-12 mb-8">
        <div className="max-w-5xl mx-auto py-10 px-6 text-center bg-muted rounded-xl">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Vous aimez cette analyse ?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {user
              ? 'Ajoutez cette propriété à votre compte pour la modifier et suivre votre parcours.'
              : 'Créez votre propre analyse en quelques clics, gratuitement.'}
          </p>
          <Button
            variant="primary"
            size="lg"
            iconName={addingToMine ? 'Loader2' : user ? 'Plus' : 'BarChart3'}
            iconPosition="right"
            onClick={ctaAction}
            disabled={addingToMine}
          >
            {addingToMine ? 'Ajout en cours...' : ctaLabel}
          </Button>
          {addError && (
            <p className="mt-4 text-sm text-destructive">{addError}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedAnalysis;
